import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Typography, Alert, Button, Chip } from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import { config } from '../../config/environment';
import { loadJitsiExternalApi, JoinPayload } from '../../services/teleconsultService';
import {
  BrowserTeleconsultRecorder,
  BrowserRecordingPhase,
} from './browserTeleconsultRecording';

/** Primeira tentativa após entrar. */
const AUTO_RECORD_INITIAL_DELAY_MS = 8000;
/** Poll até paciente entrar (2 participantes = profissional + paciente). */
const AUTO_RECORD_POLL_MS = 5000;
/** Jibri: espera RTP após paciente entrar. */
const AUTO_RECORD_AFTER_PATIENT_MS = 20_000;
/** Browser: após paciente, pede compartilhar aba com áudio. */
const AUTO_BROWSER_AFTER_PATIENT_MS = 6000;
/** Após este tempo, mostra aviso manual mas continua a tentar detectar o paciente. */
const AUTO_RECORD_MANUAL_HINT_MS = 120_000;

interface TeleconsultaRoomProps {
  join: JoinPayload;
  /** Mostrado no topo (Ex.: "Teleconsulta — Dra. Ana" ou "Sua consulta com..."). */
  title?: string;
  /** Texto descritivo (subtítulo). */
  subtitle?: string;
  /** Callback quando o participante sai da sala. */
  onLeft?: () => void;
  /** Callback ao carregar a sala. */
  onReady?: () => void;
  /** Callback quando a conferência Jitsi fica ativa (início do contador de chamada). */
  onConferenceJoined?: () => void;
  /** Texto exibido após sair da sala (ex.: orientação ao paciente). */
  afterLeftMessage?: string;
  /**
   * `fullscreen` — ocupa a viewport (paciente / uso isolado).
   * `embedded` — encaixa num flex pai (teleconsulta profissional + painel lateral).
   */
  layout?: 'fullscreen' | 'embedded';
  /**
   * Inicia gravação Jibri (ficheiro) ao entrar na sala — legado; requer Jibri no servidor.
   */
  autoStartFileRecording?: boolean;
  /** Gravação no browser (áudio da aba). Só profissional. */
  useBrowserRecording?: boolean;
  /** Inicia gravação browser automaticamente quando o paciente entrar. */
  autoStartBrowserRecording?: boolean;
  /** Paciente: só sai da sala; profissional: pode encerrar para todos. */
  participantRole?: 'professional' | 'patient';
}

/**
 * Renderiza a sala Jitsi usando JitsiMeetExternalAPI.
 * - Conecta ao domain do servidor self-hosted recebido no payload.
 * - Inclui JWT (?jwt=...) automaticamente.
 * - Limpa a instância no unmount.
 */
const TeleconsultaRoom: React.FC<TeleconsultaRoomProps> = ({
  join,
  title,
  subtitle,
  onLeft,
  onReady,
  onConferenceJoined,
  afterLeftMessage,
  layout = 'fullscreen',
  autoStartFileRecording = false,
  useBrowserRecording: useBrowserRecordingProp,
  autoStartBrowserRecording: autoStartBrowserRecordingProp,
  participantRole = 'professional',
}) => {
  const embedded = layout === 'embedded';
  const isPatient = participantRole === 'patient';
  const useBrowserRecording =
    !isPatient &&
    (useBrowserRecordingProp ?? config.teleconsult.browserRecording) &&
    !autoStartFileRecording;
  const autoStartBrowserRecording =
    useBrowserRecording &&
    (autoStartBrowserRecordingProp ?? config.teleconsult.autoStartBrowserRecording);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const apiRef = useRef<any>(null);
  const browserRecorderRef = useRef<BrowserTeleconsultRecorder | null>(null);
  const autoRecordAttemptedRef = useRef(false);
  const autoBrowserAttemptedRef = useRef(false);
  const autoRecordTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autoRecordPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const autoRecordMaxWaitRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const disposedRef = useRef(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [left, setLeft] = useState(false);
  const [recordingState, setRecordingState] = useState<
    'idle' | 'pending' | 'active' | 'failed'
  >('idle');
  const [recordingHint, setRecordingHint] = useState('');
  const [browserPhase, setBrowserPhase] = useState<BrowserRecordingPhase>('idle');
  const [browserHint, setBrowserHint] = useState('');

  const getBrowserRecorder = useCallback(() => {
    if (!browserRecorderRef.current) {
      browserRecorderRef.current = new BrowserTeleconsultRecorder();
    }
    const rec = browserRecorderRef.current;
    rec.onCaptureEnded = () => {
      setBrowserHint(
        'O compartilhamento da aba foi encerrado. Clique em "Parar e enviar" para enviar o que foi gravado até aqui.',
      );
    };
    return rec;
  }, []);

  const startBrowserRecording = useCallback(async () => {
    if (isPatient || !useBrowserRecording) return;
    try {
      setBrowserPhase('awaiting_share');
      setBrowserHint(
        'Escolha esta aba e marque "Compartilhar áudio da aba" para gravar a consulta.',
      );
      await getBrowserRecorder().start();
      setBrowserPhase('recording');
      setBrowserHint('Gravando áudio da consulta. Mantenha esta aba aberta.');
    } catch (e: any) {
      setBrowserPhase('failed');
      setBrowserHint(
        e?.message ||
          'Não foi possível iniciar a gravação. Use o botão Gravar quando o paciente estiver na sala.',
      );
    }
  }, [getBrowserRecorder, isPatient, useBrowserRecording]);

  const stopBrowserRecording = useCallback(async () => {
    if (!useBrowserRecording) return;
    if (browserPhase !== 'recording') {
      setBrowserHint('Não há gravação ativa para enviar.');
      return;
    }
    const recorder = getBrowserRecorder();
    if (!recorder.isRecording() && !recorder.hasCapturedData()) {
      setBrowserPhase('failed');
      setBrowserHint(
        'Nenhum áudio capturado. Inicie de novo e marque "Compartilhar áudio da aba" ao escolher esta página.',
      );
      return;
    }
    try {
      setBrowserPhase('uploading');
      setBrowserHint('Enviando gravação…');
      await recorder.stopAndUpload(join.appointmentId);
      setBrowserPhase('done');
      setBrowserHint('Gravação enviada. A transcrição será processada em seguida.');
    } catch (e: any) {
      setBrowserPhase('failed');
      const msg =
        e?.response?.data?.error ||
        e?.message ||
        'Falha ao enviar a gravação.';
      setBrowserHint(msg);
      console.error('[teleconsult] stopBrowserRecording', e);
    }
  }, [browserPhase, getBrowserRecorder, join.appointmentId, useBrowserRecording]);

  /** Desmuta o microfone local — logs Jicofo com audioMuted=true geram gravação muda (-91 dB). */
  const ensureLocalAudioUnmuted = useCallback(async (api: {
    isAudioMuted?: () => Promise<boolean>;
    executeCommand: (cmd: string, ...args: unknown[]) => void;
  }) => {
    try {
      if (typeof api.isAudioMuted === 'function') {
        const muted = await api.isAudioMuted();
        if (muted) api.executeCommand('toggleAudio');
        return;
      }
    } catch {
      // segue
    }
    try {
      api.executeCommand('setAudioMuted', false);
    } catch {
      // API antiga: sem setAudioMuted
    }
  }, []);

  const requestFileRecording = useCallback(async () => {
    const api = apiRef.current;
    if (!api) return;
    try {
      await ensureLocalAudioUnmuted(api);
      setRecordingState('pending');
      setRecordingHint('Solicitando gravação da consulta (áudio da sala inteira)…');
      api.executeCommand('startRecording', { mode: 'file' });
    } catch (e: any) {
      setRecordingState('failed');
      setRecordingHint(
        e?.message || 'Não foi possível iniciar a gravação. Use o botão Gravar na barra do Jitsi.',
      );
    }
  }, [ensureLocalAudioUnmuted, isPatient]);

  const clearAutoRecordSchedulers = useCallback(() => {
    if (autoRecordTimerRef.current) {
      clearTimeout(autoRecordTimerRef.current);
      autoRecordTimerRef.current = null;
    }
    if (autoRecordPollRef.current) {
      clearInterval(autoRecordPollRef.current);
      autoRecordPollRef.current = null;
    }
    if (autoRecordMaxWaitRef.current) {
      clearTimeout(autoRecordMaxWaitRef.current);
      autoRecordMaxWaitRef.current = null;
    }
  }, []);

  /** Paciente presente = pelo menos 1 participante remoto (ou total ≥ 2 incluindo o profissional). */
  const hasPatientInConference = useCallback(async (api: {
    getNumberOfParticipants?: () => Promise<number>;
    getParticipantsInfo?: () => Array<{ local?: boolean }>;
  }): Promise<boolean> => {
    try {
      if (typeof api.getParticipantsInfo === 'function') {
        const infos = api.getParticipantsInfo();
        if (Array.isArray(infos)) {
          const remotes = infos.filter((p) => !p?.local);
          if (remotes.length >= 1) return true;
        }
      }
    } catch {
      // segue para fallback
    }

    if (typeof api.getNumberOfParticipants !== 'function') {
      return true;
    }

    const count = await api.getNumberOfParticipants();
    return count >= 2;
  }, []);

  const maybeStartAutoRecording = useCallback(() => {
    if (disposedRef.current || autoRecordAttemptedRef.current || !autoStartFileRecording) return;
    const api = apiRef.current;
    if (!api) return;

    const start = () => {
      if (disposedRef.current || autoRecordAttemptedRef.current) return;
      autoRecordAttemptedRef.current = true;
      clearAutoRecordSchedulers();
      setRecordingHint(
        'Paciente na sala — confirme que os dois microfones estão ligados. Gravação em ~20 s…',
      );
      autoRecordTimerRef.current = setTimeout(() => {
        if (!disposedRef.current) void requestFileRecording();
      }, AUTO_RECORD_AFTER_PATIENT_MS);
    };

    void hasPatientInConference(api).then((ready) => {
      if (disposedRef.current || autoRecordAttemptedRef.current) return;
      if (ready) {
        start();
      } else {
        setRecordingHint(
          'Aguardando o paciente na sala. A gravação inicia automaticamente quando ele entrar.',
        );
      }
    });
  }, [
    autoStartFileRecording,
    clearAutoRecordSchedulers,
    hasPatientInConference,
    requestFileRecording,
  ]);

  const maybeStartAutoBrowserRecording = useCallback(() => {
    if (
      disposedRef.current ||
      autoBrowserAttemptedRef.current ||
      !autoStartBrowserRecording ||
      browserPhase === 'recording' ||
      browserPhase === 'uploading'
    ) {
      return;
    }
    const api = apiRef.current;
    if (!api) return;

    const start = () => {
      if (disposedRef.current || autoBrowserAttemptedRef.current) return;
      autoBrowserAttemptedRef.current = true;
      clearAutoRecordSchedulers();
      setBrowserHint(
        'Paciente na sala — em instantes pediremos compartilhar o áudio desta aba.',
      );
      autoRecordTimerRef.current = setTimeout(() => {
        if (!disposedRef.current) void startBrowserRecording();
      }, AUTO_BROWSER_AFTER_PATIENT_MS);
    };

    void hasPatientInConference(api).then((ready) => {
      if (disposedRef.current || autoBrowserAttemptedRef.current) return;
      if (ready) {
        start();
      } else {
        setBrowserHint(
          'Aguardando o paciente. A gravação inicia quando ele entrar (compartilhe o áudio da aba).',
        );
      }
    });
  }, [
    autoStartBrowserRecording,
    browserPhase,
    clearAutoRecordSchedulers,
    hasPatientInConference,
    startBrowserRecording,
  ]);

  const scheduleAutoRecording = useCallback(() => {
    if (!autoStartFileRecording || autoRecordAttemptedRef.current) return;
    clearAutoRecordSchedulers();
    setRecordingHint(
      'A gravação automática começa quando o paciente entrar na sala.',
    );

    autoRecordTimerRef.current = setTimeout(() => {
      maybeStartAutoRecording();
    }, AUTO_RECORD_INITIAL_DELAY_MS);

    autoRecordPollRef.current = setInterval(() => {
      maybeStartAutoRecording();
    }, AUTO_RECORD_POLL_MS);

    // Só troca o texto; não para o poll — o paciente pode entrar depois dos 2 min
    autoRecordMaxWaitRef.current = setTimeout(() => {
      if (!autoRecordAttemptedRef.current && !disposedRef.current) {
        setRecordingHint(
          'Se a gravação não iniciou sozinha, use o botão Gravar na barra do vídeo (com os dois na sala).',
        );
      }
    }, AUTO_RECORD_MANUAL_HINT_MS);
  }, [autoStartFileRecording, clearAutoRecordSchedulers, maybeStartAutoRecording]);

  const scheduleAutoBrowser = useCallback(() => {
    if (!autoStartBrowserRecording || autoBrowserAttemptedRef.current) return;
    clearAutoRecordSchedulers();
    setBrowserHint('A gravação começa quando o paciente entrar (áudio desta aba).');

    autoRecordTimerRef.current = setTimeout(() => {
      maybeStartAutoBrowserRecording();
    }, AUTO_RECORD_INITIAL_DELAY_MS);

    autoRecordPollRef.current = setInterval(() => {
      maybeStartAutoBrowserRecording();
    }, AUTO_RECORD_POLL_MS);

    autoRecordMaxWaitRef.current = setTimeout(() => {
      if (!autoBrowserAttemptedRef.current && !disposedRef.current) {
        setBrowserHint('Use o botão Gravar abaixo quando o paciente estiver na sala.');
      }
    }, AUTO_RECORD_MANUAL_HINT_MS);
  }, [
    autoStartBrowserRecording,
    clearAutoRecordSchedulers,
    maybeStartAutoBrowserRecording,
  ]);

  const showRecordingUi = autoStartFileRecording || useBrowserRecording;

  useEffect(() => {
    disposedRef.current = false;

    const init = async () => {
      try {
        const JitsiMeetExternalAPI = await loadJitsiExternalApi(join.domain);
        if (disposedRef.current) return;
        if (!containerRef.current) return;

        const toolbarProfessional = [
          'microphone',
          'camera',
          'desktop',
          'fullscreen',
          'hangup',
          'chat',
          'settings',
          'raisehand',
          'videoquality',
          'tileview',
          'select-background',
          ...(useBrowserRecording ? [] : ['recording']),
        ];

        const toolbarPatient = [
          'microphone',
          'camera',
          'desktop',
          'fullscreen',
          'hangup',
          'chat',
          'settings',
          'raisehand',
          'videoquality',
          'tileview',
          'select-background',
        ];

        const options = {
          roomName: join.roomName,
          jwt: join.token,
          parentNode: containerRef.current,
          width: '100%',
          height: '100%',
          userInfo: {
            displayName: join.displayName,
          },
          configOverwrite: {
            prejoinPageEnabled: false,
            disableDeepLinking: true,
            disableInviteFunctions: true,
            enableWelcomePage: false,
            enableClosePage: false,
            startWithAudioMuted: false,
            startWithVideoMuted: false,
            startSilent: false,
            enableNoisyMicDetection: false,
            channelLastN: -1,
            toolbarButtons: isPatient ? toolbarPatient : toolbarProfessional,
            ...(isPatient
              ? {
                  enableEndConference: false,
                  disableModeratorIndicator: true,
                  disableRemoteMute: true,
                  disableGrantModerator: true,
                  buttonsWithNotifyClick: [
                    { key: 'hangup', preventExecution: true },
                    { key: 'hangup-menu', preventExecution: true },
                    { key: 'end-meeting', preventExecution: true },
                  ],
                }
              : {}),
          },
          interfaceConfigOverwrite: {
            DEFAULT_LOGO_URL: '',
            DEFAULT_WELCOME_PAGE_LOGO_URL: '',
            SHOW_JITSI_WATERMARK: false,
            SHOW_WATERMARK_FOR_GUESTS: false,
            SHOW_BRAND_WATERMARK: false,
            DISABLE_VIDEO_BACKGROUND: false,
            MOBILE_APP_PROMO: false,
            HIDE_INVITE_MORE_HEADER: true,
            JITSI_WATERMARK_LINK: '',
          },
        };

        const api = new JitsiMeetExternalAPI(join.domain, options);
        apiRef.current = api;

        if (isPatient) {
          api.addListener('toolbarButtonClicked', (payload: { key?: string }) => {
            const key = String(payload?.key ?? '');
            if (key === 'hangup' || key === 'hangup-menu') {
              try {
                api.executeCommand('hangup');
              } catch {
                // ignore
              }
            }
          });
        }

        api.addListener('videoConferenceJoined', () => {
          setLoading(false);
          onConferenceJoined?.();
          onReady?.();
          void ensureLocalAudioUnmuted(api);
          if (autoStartFileRecording) scheduleAutoRecording();
          else if (autoStartBrowserRecording) scheduleAutoBrowser();
        });

        api.addListener('participantJoined', () => {
          maybeStartAutoRecording();
          maybeStartAutoBrowserRecording();
        });

        api.addListener('participantLeft', () => {
          if (autoRecordAttemptedRef.current || disposedRef.current) return;
          void hasPatientInConference(api).then((ready) => {
            if (disposedRef.current || autoRecordAttemptedRef.current) return;
            if (!ready) {
              setRecordingHint(
                'Aguardando o paciente na sala. A gravação inicia automaticamente quando ele entrar.',
              );
            }
          });
        });

        api.addListener('recordingStatusChanged', (payload: Record<string, unknown>) => {
          const mode = String(payload?.mode ?? '');
          const status = String(payload?.status ?? payload?.on ?? '').toLowerCase();
          const err = payload?.error as string | undefined;
          if (mode && mode !== 'file') return;

          if (status === 'on' || status === 'true' || status === 'started') {
            setRecordingState('active');
            setRecordingHint(
              'Gravação ativa — profissional e paciente (transcrição após a consulta).',
            );
          } else if (status === 'off' || status === 'false' || status === 'stopped') {
            setRecordingState((prev) => {
              if (prev === 'active') setRecordingHint('');
              return prev === 'active' ? 'idle' : prev;
            });
          } else if (err || status === 'failed' || status === 'error') {
            setRecordingState('failed');
            setRecordingHint(
              typeof err === 'string' && err
                ? err
                : 'Gravação indisponível. Verifique Jibri no servidor ou use o botão Gravar.',
            );
          }
        });

        api.addListener('readyToClose', () => {
          setLeft(true);
          onLeft?.();
        });

        api.addListener('videoConferenceLeft', () => {
          setLeft(true);
          onLeft?.();
        });
      } catch (e: any) {
        if (!disposedRef.current) {
          setError(e?.message || 'Erro ao iniciar teleconsulta.');
          setLoading(false);
        }
      }
    };

    init();

    return () => {
      disposedRef.current = true;
      clearAutoRecordSchedulers();
      autoRecordAttemptedRef.current = false;
      autoBrowserAttemptedRef.current = false;
      browserRecorderRef.current?.dispose();
      browserRecorderRef.current = null;
      try {
        apiRef.current?.dispose?.();
      } catch {
        // ignore
      }
      apiRef.current = null;
    };
    // join intencionalmente como dependência única — recarrega se trocar
  }, [
    join,
    onLeft,
    onReady,
    onConferenceJoined,
    autoStartFileRecording,
    autoStartBrowserRecording,
    clearAutoRecordSchedulers,
    maybeStartAutoRecording,
    maybeStartAutoBrowserRecording,
    scheduleAutoRecording,
    scheduleAutoBrowser,
    hasPatientInConference,
    isPatient,
    useBrowserRecording,
  ]);

  const clinicalEmbedded = embedded;

  if (left) {
    return (
      <Box
        sx={{
          minHeight: clinicalEmbedded ? '100%' : '100vh',
          flex: clinicalEmbedded ? 1 : undefined,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: 2,
          textAlign: 'center',
          px: 3,
          ...(clinicalEmbedded
            ? { bgcolor: '#f8fafc', color: 'text.primary' }
            : { bgcolor: '#0f172a', color: '#fff' }),
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700, color: clinicalEmbedded ? '#0f172a' : '#fff' }}>
          Teleconsulta encerrada
        </Typography>
        <Typography
          sx={{
            maxWidth: 480,
            color: clinicalEmbedded ? '#64748b' : undefined,
            opacity: clinicalEmbedded ? 1 : 0.8,
          }}
        >
          {afterLeftMessage || 'A chamada foi encerrada. Você pode fechar esta janela.'}
        </Typography>
      </Box>
    );
  }

  const headerBlock = (title || subtitle || showRecordingUi) && (
    <Box
      sx={{
        px: clinicalEmbedded ? { xs: 2, sm: 2.5 } : 2,
        py: clinicalEmbedded ? 1.5 : 1,
        flexShrink: 0,
        ...(clinicalEmbedded
          ? {
              bgcolor: 'background.paper',
              color: 'text.primary',
              borderBottom: '1px solid',
              borderColor: 'divider',
            }
          : {
              color: '#fff',
              borderBottom: '1px solid rgba(255,255,255,0.08)',
            }),
      }}
    >
      {clinicalEmbedded ? (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              background: 'linear-gradient(135deg, #0f766e 0%, #14b8a6 100%)',
              color: '#fff',
            }}
          >
            <VideocamOutlinedIcon fontSize="small" />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            {title && (
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem', lineHeight: 1.25 }}>
                {title}
              </Typography>
            )}
            {subtitle && (
              <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 0.25 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
        </Box>
      ) : (
        <>
          {title && (
            <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }}>{title}</Typography>
          )}
          {subtitle && (
            <Typography sx={{ opacity: 0.75, fontSize: '0.78rem' }}>{subtitle}</Typography>
          )}
        </>
      )}
      {showRecordingUi && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 0.75,
            mt: clinicalEmbedded ? 1.25 : 0.5,
          }}
        >
          {useBrowserRecording && browserPhase === 'recording' && (
            <Chip
              size="small"
              icon={<FiberManualRecordIcon sx={{ color: '#dc2626 !important' }} />}
              label="Gravando consulta"
              sx={{
                bgcolor: '#fef2f2',
                color: '#b91c1c',
                fontWeight: 600,
                border: '1px solid #fecaca',
              }}
            />
          )}
          {useBrowserRecording && browserPhase === 'uploading' && (
            <Chip size="small" label="Enviando gravação…" sx={{ fontWeight: 600 }} />
          )}
          {useBrowserRecording && browserPhase === 'done' && (
            <Chip
              size="small"
              label="Gravação enviada"
              sx={{ bgcolor: '#f0fdfa', color: '#0f766e', fontWeight: 600 }}
            />
          )}
          {autoStartFileRecording && recordingState === 'active' && (
            <Chip
              size="small"
              icon={<FiberManualRecordIcon sx={{ color: '#dc2626 !important' }} />}
              label="Gravando (Jibri)"
              sx={{
                bgcolor: '#fef2f2',
                color: '#b91c1c',
                fontWeight: 600,
                border: '1px solid #fecaca',
              }}
            />
          )}
          {autoStartFileRecording && recordingState === 'pending' && (
            <Chip
              size="small"
              label="Iniciando gravação…"
              sx={{ bgcolor: '#f0fdfa', color: '#0f766e', fontWeight: 600 }}
            />
          )}
          {useBrowserRecording && (
            <>
              {browserPhase !== 'recording' && browserPhase !== 'uploading' && (
                <Button
                  size="small"
                  variant="contained"
                  color="error"
                  disabled={browserPhase === 'awaiting_share'}
                  onClick={() => void startBrowserRecording()}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  Gravar
                </Button>
              )}
              {browserPhase === 'recording' && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => void stopBrowserRecording()}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  Parar e enviar
                </Button>
              )}
            </>
          )}
          {(browserHint || recordingHint) && (
            <Typography
              variant="caption"
              sx={{
                color: clinicalEmbedded ? '#64748b' : undefined,
                opacity: clinicalEmbedded ? 1 : 0.7,
                flex: '1 1 100%',
              }}
            >
              {browserHint || recordingHint}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );

  return (
    <Box
      sx={{
        ...(clinicalEmbedded
          ? {
              position: 'relative',
              flex: 1,
              minHeight: 0,
              height: '100%',
              bgcolor: 'background.paper',
            }
          : {
              position: 'fixed',
              inset: 0,
              bgcolor: '#0f172a',
            }),
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {headerBlock}

      {browserPhase === 'failed' && useBrowserRecording && !error && (
        <Alert
          severity="warning"
          sx={{
            mx: clinicalEmbedded ? 2 : 2,
            mt: clinicalEmbedded ? 0 : 1,
            flexShrink: 0,
            borderRadius: clinicalEmbedded ? 2 : undefined,
          }}
          action={
            <Button color="inherit" size="small" onClick={() => void startBrowserRecording()}>
              Tentar de novo
            </Button>
          }
        >
          {browserHint || 'Gravação falhou. Use Gravar e compartilhe o áudio da aba.'}
        </Alert>
      )}

      {recordingState === 'failed' && autoStartFileRecording && !error && (
        <Alert
          severity="warning"
          sx={{
            mx: clinicalEmbedded ? 2 : 2,
            mt: clinicalEmbedded ? 0 : 1,
            flexShrink: 0,
            borderRadius: clinicalEmbedded ? 2 : undefined,
          }}
          action={
            <Button color="inherit" size="small" onClick={requestFileRecording}>
              Tentar de novo
            </Button>
          }
        >
          {recordingHint || 'Gravação automática falhou. Use o botão Gravar na barra do vídeo.'}
        </Alert>
      )}

      {error && (
        <Box sx={{ p: clinicalEmbedded ? 2 : 3, color: clinicalEmbedded ? 'text.primary' : '#fff' }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Button variant="outlined" onClick={() => window.location.reload()}>
            Tentar novamente
          </Button>
        </Box>
      )}

      <Box
        sx={{
          position: 'relative',
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          ...(clinicalEmbedded
            ? {
                mx: 2,
                mb: 2,
                borderRadius: 2,
                overflow: 'hidden',
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: '#111827',
                boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.06)',
              }
            : {}),
        }}
      >
        {loading && !error && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              color: '#fff',
              zIndex: 1,
              bgcolor: 'rgba(17,24,39,0.88)',
            }}
          >
            <CircularProgress sx={{ color: '#14B8A6' }} />
            <Typography variant="body2">Conectando à sala…</Typography>
          </Box>
        )}
        <Box ref={containerRef} sx={{ flex: 1, minHeight: 0 }} />
      </Box>
    </Box>
  );
};

export default TeleconsultaRoom;
